import React, { useEffect, useRef } from "react";
import "../../styles/NavigationSlideOver.css";
import LeftArrowIcon from "../../assets/common/left_navigation_arrow.svg";
import redXIcon from "../../assets/common/red_x_icon.svg";

 interface NavigationSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  /** Parent page label shown first in the breadcrumb, e.g. "Accounts" */
  parentPage: string;
  /** Optional sub-page label shown after the separator, e.g. "Flight Risk Assessment" */
  subPage?: string;
  /** Large title text shown below the breadcrumb, e.g. account name */
  title: string;
  /** Optional badge/pill element shown next to the title */
  badge?: React.ReactNode;
  headerMeta?: string;
  slideOverWidth?: string;
  /** Optional action buttons rendered in the top-right of the header (e.g. Edit / Update / Create) */
  headerActions?: React.ReactNode;
  /** When true, hides the X close button in the header */
  hideCloseButton?: boolean;
  /** Scrollable content area rendered below the header */
  children: React.ReactNode;
} 

const NavigationSlideOver: React.FC<NavigationSlideOverProps> = ({
  isOpen,
  onClose,
  parentPage,
  subPage,
  title,
  badge,
  headerMeta,
  headerActions,
  hideCloseButton = false,
  children,
  slideOverWidth = "90%",
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Reset scroll position when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        contentRef.current?.scrollTo(0, 0);
      }, 30);
    }
  }, [isOpen]);

  // Close on Escape key (disabled when editing/creating)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !hideCloseButton) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, hideCloseButton]);

  return (
    <>
      {isOpen && (
        <div
          className="nso-overlay"
          onClick={hideCloseButton ? undefined : onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={`nso-panel ${isOpen ? "nso-panel--open" : ""}`}
        role="dialog"
        aria-modal={isOpen ? "true" : undefined}
        aria-hidden={!isOpen}
        aria-label={title}
        style={{ width: slideOverWidth }}
      >
        <div className="nso-header">
          {/* Header actions (e.g. Edit / Discard / Update) */}
          {headerActions && (
            <div
              className={`nso-header-actions${hideCloseButton ? " nso-header-actions--no-close" : ""}`}
            >
              {headerActions}
            </div>
          )}

          {!hideCloseButton && (
            <button
              className="nso-close-btn"
              onClick={onClose}
              aria-label="Close panel"
            >
              <img src={redXIcon} alt="" />
            </button>
          )}

          {/* Breadcrumb */}
          <nav className="nso-breadcrumb" aria-label="Breadcrumb">
            <button
              type="button"
              className="nso-breadcrumb__btn"
              onClick={hideCloseButton ? undefined : onClose}
              disabled={hideCloseButton}
              aria-label={`Back to ${parentPage}`}
            >
              <img
                src={LeftArrowIcon}
                alt=""
                className="nso-back_icon"
                aria-hidden="true"
              />

              {/* Parent page — always shown */}
              <span className="nso-breadcrumb__label nso_parentpage">
                {parentPage}
              </span>
            </button>

            {/* Sub-page — only shown if provided */}
            {subPage && (
              <>
                <span className="nso-breadcrumb__sep">/</span>
                <span className="nso-breadcrumb__label nso_subpage">
                  {subPage}
                </span>
              </>
            )}
          </nav>

          {/* Title row: name + badge + meta */}
          <div className="nso-title-row">
            <h1 className="nso-title">{title}</h1>
            {badge && badge}
            {headerMeta && (
              <span className="nso-header-meta">{headerMeta}</span>
            )}
          </div>
        </div>

        <div ref={contentRef} className="nso-content">
          {children}
        </div>
      </div>
    </>
  );
};

export default NavigationSlideOver;
